import React from "react";
import { Document, Page, Path, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import { commonStyles, formatPhoneNumber } from "../../Services/ReactPDFService.jsx";

// Clean Professional Cover Letter Template - Clean design with left border accent
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    fontFamily: "Roboto",
    backgroundColor: "#ffffff",
    paddingHorizontal: 40,
    paddingTop: 20,
    paddingBottom: 20,
  },
  // Header section
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#000000",
  },
  headerLeft: {
    flex: 1,
  },
  // Name styling
  name: {
    fontSize: 32,
    fontWeight: 700,
    color: "#000000",
    letterSpacing: 0.5,
  },
  // Contact info on right side with icons
  contactRight: {
    alignItems: "flex-end",
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  contactText: {
    fontSize: 10,
    color: "#333333",
    marginRight: 10,
    textAlign: "right",
  },
  // Icon container
  iconContainer: {
    width: 24,
    height: 24,
    backgroundColor: "#f0f0f0",
    borderRadius: 4,
    justifyContent: "center",
    alignItems: "center",
  },
  contactIcon: {
    width: 12,
    height: 12,
  },
  // Main content area with left border
  contentWrapper: {
    paddingTop: 10,
  },
  // Body text styles
  bodyText: {
    fontSize: 11,
    lineHeight: 1.7,
    color: "#374151",
    textAlign: "justify",
    marginBottom: 14,
  },
});

// Icon component
const IconBox = ({ children }) => <View style={styles.iconContainer}>{children}</View>;

const CleanProfessionalCoverLetter = ({ coverLetterData }) => {
  const { header, generatedContent } = coverLetterData || {};
  const { name, email, phone, location } = header || {};

  // Parse content into paragraphs
  const paragraphs = generatedContent ? generatedContent.split("\n\n").filter((p) => p.trim()) : [];

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap={true}>
        {/* Header Section */}
        <View style={styles.headerContainer}>
          {/* Name on left */}
          <View style={styles.headerLeft}>
            <Text style={styles.name}>{name}</Text>
          </View>

          {/* Contact info on right with icons */}
          <View style={styles.contactRight}>
            {/* Phone */}
            {phone && (
              <View style={styles.contactItem}>
                <Text style={styles.contactText}>{formatPhoneNumber(phone)}</Text>
                <IconBox>
                  <Svg style={styles.contactIcon} viewBox="0 0 24 24">
                    <Path
                      d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
                      fill="#333333"
                    />
                  </Svg>
                </IconBox>
              </View>
            )}

            {/* Email */}
            {email && (
              <View style={styles.contactItem}>
                <Text style={styles.contactText}>{email}</Text>
                <IconBox>
                  <Svg style={styles.contactIcon} viewBox="0 0 24 24">
                    <Path
                      d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                      fill="#333333"
                    />
                  </Svg>
                </IconBox>
              </View>
            )}

            {/* Location */}
            {location && (
              <View style={styles.contactItem}>
                <Text style={styles.contactText}>{location}</Text>
                <IconBox>
                  <Svg style={styles.contactIcon} viewBox="0 0 24 24">
                    <Path
                      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                      fill="#333333"
                    />
                  </Svg>
                </IconBox>
              </View>
            )}
          </View>
        </View>

        {/* Main Content with left border */}
        <View style={styles.contentWrapper}>
          {/* Body paragraphs */}
          {paragraphs.map((paragraph, index) => (
            <Text key={index} style={styles.bodyText}>
              {paragraph.trim()}
            </Text>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default CleanProfessionalCoverLetter;
